import { Outlet, createRootRoute, createRoute, createRouter, redirect } from "@tanstack/react-router";
import { configQuery, meQuery, queryClient } from "@web/lib/session";
import { Accounting, parseAccountingSearch } from "./screens/Accounting";
import { BookingDetail } from "./screens/BookingDetail";
import { Bookings, parseBookingsSearch } from "./screens/Bookings";
import { Calendar, parseCalendarSearch } from "./screens/Calendar";
import { DevUi } from "./screens/DevUi";
import { LeadDetail } from "./screens/LeadDetail";
import { Leads, parseLeadsSearch } from "./screens/Leads";
import { MessageThread } from "./screens/MessageThread";
import { Messages } from "./screens/Messages";
import { More } from "./screens/More";
import { Settings } from "./screens/Settings";
import { SettingsApiKeys } from "./screens/SettingsApiKeys";
import { SettingsCatalog } from "./screens/SettingsCatalog";
import { SettingsCompany } from "./screens/SettingsCompany";
import { SettingsEventTypes, SettingsLeadSources } from "./screens/SettingsLists";
import { SettingsUsers } from "./screens/SettingsUsers";
import { SettingsVenues } from "./screens/SettingsVenues";
import { Shell } from "./screens/Shell";
import { SignIn } from "./screens/SignIn";
import { Today } from "./screens/Today";
import { ContractsCountersign } from "./screens/ContractsCountersign";
import { SettingsTemplates } from "./screens/SettingsTemplates";
import { Queue, type QueueSearch } from "./screens/Queue";
import { Vendors, type VendorsSearch } from "./screens/Vendors";
import { SettingsVendorCategories } from "./screens/SettingsVendorCategories";
import { Commissions, SettingsPayments, parseCommissionsSearch } from "./screens/Commissions";
import { SettingsAutomation } from "./screens/SettingsAutomation";
import { SettingsCalendar } from "./screens/SettingsCalendar";
import { SettingsDripSequences } from "./screens/SettingsDripSequences";
import { SettingsMessageTemplates } from "./screens/SettingsMessageTemplates";
import { Events, Tours } from "./screens/Schedule";
import { parseScheduleSearch } from "./lib/scheduleFilters";

const rootRoute = createRootRoute({ component: Outlet });

// ---- public
const signInRoute = createRoute({ getParentRoute: () => rootRoute, path: "/signin", component: SignIn });
// Staging-only gallery (SPEC §6.2): production redirects to sign-in.
const devUiRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/dev/ui",
  beforeLoad: async () => {
    const cfg = await queryClient.ensureQueryData(configQuery).catch(() => null);
    if (cfg?.env !== "staging") throw redirect({ to: "/signin", replace: true });
  },
  component: DevUi,
});

// ---- authenticated shell: loads /api/me once; any failure (401, no Worker) → /signin
const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "app",
  beforeLoad: async () => {
    try {
      await queryClient.ensureQueryData(meQuery);
    } catch {
      throw redirect({ to: "/signin", replace: true });
    }
  },
  component: Shell,
});

const indexRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/",
  beforeLoad: () => {
    throw redirect({ to: "/today", replace: true });
  },
});
const app = <P extends string>(path: P, component: () => React.JSX.Element) => createRoute({ getParentRoute: () => appRoute, path, component });
/** Leads takes `?sort=&status=&q=` (Today's "See all" links, and the screen's own
 * controls, which write every one of them back). */
const leadsRoute = createRoute({ getParentRoute: () => appRoute, path: "/leads", component: Leads, validateSearch: parseLeadsSearch });

/** The queue's filters are part of the address, so a refresh keeps them. */
const queueRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/more/queue",
  validateSearch: (search: Record<string, unknown>): QueueSearch => ({
    kind: typeof search.kind === "string" && search.kind ? search.kind : undefined,
    channel: search.channel === "sms" || search.channel === "email" ? search.channel : undefined,
    q: typeof search.q === "string" && search.q ? search.q : undefined,
  }),
  component: Queue,
});

const vendorsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/more/vendors",
  validateSearch: (search: Record<string, unknown>): VendorsSearch => ({
    q: typeof search.q === "string" && search.q ? search.q : undefined,
    categoryId: typeof search.categoryId === "string" && search.categoryId ? search.categoryId : undefined,
    venueId: typeof search.venueId === "string" && search.venueId ? search.venueId : undefined,
    preferred: search.preferred === "1" ? "1" : undefined,
  }),
  component: Vendors,
});

const commissionsRoute = createRoute({ getParentRoute: () => appRoute, path: "/more/commissions", component: Commissions, validateSearch: parseCommissionsSearch });
const accountingRoute = createRoute({ getParentRoute: () => appRoute, path: "/more/accounting", component: Accounting, validateSearch: parseAccountingSearch });
const calendarRoute = createRoute({ getParentRoute: () => appRoute, path: "/calendar", component: Calendar, validateSearch: parseCalendarSearch });
const toursRoute = createRoute({ getParentRoute: () => appRoute, path: "/tours", component: Tours, validateSearch: parseScheduleSearch });
const eventsRoute = createRoute({ getParentRoute: () => appRoute, path: "/events", component: Events, validateSearch: parseScheduleSearch });
const bookingsRoute = createRoute({ getParentRoute: () => appRoute, path: "/bookings", component: Bookings, validateSearch: parseBookingsSearch });

const routeTree = rootRoute.addChildren([
  signInRoute,
  devUiRoute,
  appRoute.addChildren([
    indexRoute,
    app("/today", Today),
    toursRoute,
    eventsRoute,
    leadsRoute,
    app("/leads/$id", LeadDetail),
    calendarRoute,
    bookingsRoute,
    app("/bookings/$id", BookingDetail),
    app("/contracts/$id/countersign", ContractsCountersign),
    app("/more", More),
    accountingRoute,
    commissionsRoute,
    queueRoute,
    app("/more/messages", Messages),
    app("/more/messages/$contactId", MessageThread),
    vendorsRoute,
    app("/settings/vendor-categories", SettingsVendorCategories),
    app("/settings", Settings),
    app("/settings/company", SettingsCompany),
    app("/settings/venues", SettingsVenues),
    app("/settings/catalog", SettingsCatalog),
    app("/settings/event-types", SettingsEventTypes),
    app("/settings/lead-sources", SettingsLeadSources),
    app("/settings/users", SettingsUsers),
    app("/settings/api-keys", SettingsApiKeys),
    app("/settings/contract-templates", SettingsTemplates),
    app("/settings/message-templates", SettingsMessageTemplates),
    app("/settings/drip-sequences", SettingsDripSequences),
    app("/settings/automation", SettingsAutomation),
    app("/settings/calendar", SettingsCalendar),
    app("/settings/payments", SettingsPayments),
  ]),
]);

export const router = createRouter({ routeTree, defaultPreload: false, scrollRestoration: true });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
